import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Check, X, CheckCircle2, MoreHorizontal, Pencil, Trash2, RotateCcw, CalendarClock, CalendarPlus, History, Clock } from "lucide-react";
import { toast } from "sonner";
import { VisitNote } from "@/components/VisitNote";

export function BatchBadge({ batch, carryover }) {
  if (carryover)
    return <Badge className="bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-50 rounded-full font-medium">Next Month</Badge>;
  if (!batch)
    return <Badge className="bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-100 rounded-full font-medium">Unassigned</Badge>;
  const colors = [
    "bg-teal-50 text-teal-700 border border-teal-200 ring-1 ring-teal-500/10",
    "bg-indigo-50 text-indigo-700 border border-indigo-200 ring-1 ring-indigo-500/10",
    "bg-violet-50 text-violet-700 border border-violet-200 ring-1 ring-violet-500/10",
    "bg-sky-50 text-sky-700 border border-sky-200 ring-1 ring-sky-500/10",
  ];
  const idx = (batch.charCodeAt(0) - 65) % colors.length;
  return <Badge className={`${colors[idx]} rounded-full font-medium`}>Batch {batch}</Badge>;
}

export function AttendanceBadge({ status }) {
  if (status === "visited")
    return <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-50 rounded-full font-medium">Visited</Badge>;
  if (status === "no_show")
    return <Badge className="bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-50 rounded-full font-medium">No-Show</Badge>;
  return <Badge className="bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-50 rounded-full font-medium">Pending</Badge>;
}

function formatShortDate(d) {
  if (!d) return null;
  const dt = new Date(d + "T00:00:00");
  if (isNaN(dt)) return d;
  return dt.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

export default function PatientTable({ patients, batches, isMaster, threshold, reload, completed, readOnly = false, orthodontists = [] }) {
  const [editing, setEditing] = useState(null);
  const [history, setHistory] = useState(null); // { patient, events, loading }
  const [second, setSecond] = useState(null);
  const [secondChoice, setSecondChoice] = useState("");
  const batchDateMap = Object.fromEntries((batches || []).map((b) => [b.label, b.date]));

  const saveSecond = async (batch) => {
    try {
      await api.post(`/patients/${second.id}/second-batch`, { batch });
      toast.success(batch ? `2nd appointment set to Batch ${batch}` : "2nd appointment removed");
      setSecond(null);
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const openHistory = async (p) => {
    setHistory({ patient: p, events: [], loading: true });
    try {
      const res = await api.get(`/patients/${p.id}/history`);
      setHistory({ patient: p, events: res.data, loading: false });
    } catch (err) {
      setHistory({ patient: p, events: [], loading: false });
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const act = async (fn, msg) => {
    try {
      const res = await fn();
      if (res?.data?.moved_to === "next_month")
        toast.info("No-show recorded — moved to next month's first batch");
      else if (res?.data?.moved_to)
        toast.info(`No-show recorded — moved to Batch ${res.data.moved_to}`);
      else if (msg) toast.success(msg);
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/patients/${editing.id}`, {
        name: editing.name,
        phone: editing.phone,
        register_number: editing.register_number,
        braces_kit: editing.braces_kit || "",
        orthodontist: editing.orthodontist || "",
      });
      toast.success("Patient updated");
      setEditing(null);
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (!patients.length)
    return (
      <div className="text-center py-16 text-slate-400 text-sm border border-dashed rounded-xl">
        No patients found.
      </div>
    );

  return (
    <>
      <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-xs">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/80 hover:bg-slate-50/80 border-b border-slate-200">
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Reg No</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Patient</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Phone</TableHead>
              {!completed && <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Batch</TableHead>}
              {!completed && <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Attendance</TableHead>}
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Missed</TableHead>
              {!readOnly && <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {patients.map((p) => (
              <TableRow key={p.id || p.register_number} data-testid={`patient-row-${p.register_number}`} className="hover:bg-slate-50/70 border-b border-slate-100 transition-colors">
                <TableCell className="font-mono text-xs text-slate-500">{p.register_number}</TableCell>
                <TableCell className="font-medium text-slate-900">
                  {p.name}
                  {p.orthodontist && (
                    <span className="block text-[11px] font-normal text-accent">{p.orthodontist}</span>
                  )}
                  {p.braces_kit && (
                    <span className="block text-[11px] font-normal text-slate-500">{p.braces_kit}</span>
                  )}
                </TableCell>
                <TableCell className="text-slate-600 tabular-nums">{p.phone}</TableCell>
                {!completed && (
                  <TableCell>
                    <BatchBadge batch={p.batch} carryover={p.carryover_next_month} />
                    {p.batch && batchDateMap[p.batch] && (
                      <span className="block text-[11px] text-slate-500 mt-1">
                        {formatShortDate(batchDateMap[p.batch])}
                      </span>
                    )}
                    {p.extra_batch && (
                      <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-violet-100 text-violet-800 text-[10px] font-medium px-1.5 py-0.5" data-testid={`second-appt-badge-${p.register_number}`}>
                        +2nd: Batch {p.extra_batch}
                      </span>
                    )}
                  </TableCell>
                )}
                {!completed && (
                  <TableCell>
                    <AttendanceBadge status={p.attendance} />
                  </TableCell>
                )}
                <TableCell>
                  {p.missed_count >= threshold ? (
                    <Badge data-testid="noshow-flag-badge" className="bg-red-100 text-red-800 hover:bg-red-100 rounded-full">
                      {p.missed_count} ⚠
                    </Badge>
                  ) : (
                    <span className="tabular-nums text-slate-600 text-sm">{p.missed_count}</span>
                  )}
                </TableCell>
                {!readOnly && (
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    {!completed && (
                      <>
                        <Select
                          value={p.batch || "none"}
                          onValueChange={(v) =>
                            act(() => api.post(`/patients/${p.id}/assign-batch`, { batch: v === "none" ? null : v }), "Batch updated")
                          }
                        >
                          <SelectTrigger data-testid={`assign-batch-${p.register_number}`} className="h-8 w-[110px] text-xs">
                            <SelectValue placeholder="Batch" />
                          </SelectTrigger>
                          <SelectContent className="bg-white">
                            <SelectItem value="none">Unassigned</SelectItem>
                            {batches.map((b) => (
                              <SelectItem key={b.label} value={b.label}>Batch {b.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          size="icon"
                          variant="outline"
                          data-testid={`verify-visited-button-${p.register_number}`}
                          title="Mark visited"
                          className="h-8 w-8 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700"
                          onClick={() => act(() => api.post(`/patients/${p.id}/attendance`, { status: "visited" }), "Marked as visited")}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="outline"
                          data-testid={`verify-noshow-button-${p.register_number}`}
                          title="Mark no-show"
                          className="h-8 w-8 text-red-600 hover:bg-red-50 hover:text-red-700"
                          onClick={() => act(() => api.post(`/patients/${p.id}/attendance`, { status: "no_show" }))}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="icon" variant="ghost" data-testid={`row-menu-${p.register_number}`} className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-white">
                        <DropdownMenuItem
                          data-testid={`history-button-${p.register_number}`}
                          onClick={() => openHistory(p)}
                        >
                          <History className="h-4 w-4 mr-2 text-slate-600" /> View History
                        </DropdownMenuItem>
                        {!completed && (
                          <DropdownMenuItem
                            data-testid={`mark-completed-button-${p.register_number}`}
                            onClick={() => act(() => api.post(`/patients/${p.id}/complete`), "Treatment completed")}
                          >
                            <CheckCircle2 className="h-4 w-4 mr-2 text-emerald-600" /> Mark Completed
                          </DropdownMenuItem>
                        )}
                        {!completed && (
                          <DropdownMenuItem
                            data-testid={`carryover-button-${p.register_number}`}
                            onClick={() => act(() => api.post(`/patients/${p.id}/carryover`), "Moved to next month")}
                          >
                            <CalendarClock className="h-4 w-4 mr-2 text-violet-600" /> Push to Next Month
                          </DropdownMenuItem>
                        )}
                        {!completed && (
                          <DropdownMenuItem
                            data-testid={`second-appt-button-${p.register_number}`}
                            onClick={() => { setSecondChoice(p.extra_batch || ""); setSecond({ ...p }); }}
                          >
                            <CalendarPlus className="h-4 w-4 mr-2 text-violet-600" /> {p.extra_batch ? "Change 2nd Appointment" : "Add 2nd Appointment"}
                          </DropdownMenuItem>
                        )}
                        {completed && isMaster && (
                          <DropdownMenuItem
                            data-testid={`restore-button-${p.register_number}`}
                            onClick={() => act(() => api.post(`/patients/${p.id}/restore`), "Restored to running")}
                          >
                            <RotateCcw className="h-4 w-4 mr-2 text-sky-600" /> Restore to Running
                          </DropdownMenuItem>
                        )}
                        {isMaster && (
                          <DropdownMenuItem
                            data-testid={`edit-button-${p.register_number}`}
                            onClick={() => setEditing({ ...p })}
                          >
                            <Pencil className="h-4 w-4 mr-2" /> Edit Details
                          </DropdownMenuItem>
                        )}
                        {isMaster && !completed && p.missed_count > 0 && (
                          <DropdownMenuItem
                            data-testid={`reset-missed-button-${p.register_number}`}
                            onClick={() => act(() => api.post(`/patients/${p.id}/reset-missed`), "Missed count reset")}
                          >
                            <RotateCcw className="h-4 w-4 mr-2" /> Reset Missed Count
                          </DropdownMenuItem>
                        )}
                        {isMaster && (
                          <DropdownMenuItem
                            data-testid={`delete-button-${p.register_number}`}
                            className="text-destructive focus:text-destructive"
                            onClick={() => act(() => api.delete(`/patients/${p.id}`), "Patient deleted")}
                          >
                            <Trash2 className="h-4 w-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-white sm:max-w-md" data-testid="edit-patient-modal">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Patient (Master Override)</DialogTitle>
            <DialogDescription>Correct this patient's details.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Full Name</Label>
                <Input
                  data-testid="edit-name-input"
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Phone</Label>
                <Input
                  data-testid="edit-phone-input"
                  value={editing.phone}
                  onChange={(e) => setEditing({ ...editing, phone: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Register Number</Label>
                <Input
                  data-testid="edit-reg-input"
                  value={editing.register_number}
                  onChange={(e) => setEditing({ ...editing, register_number: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Braces Kit / Appliance</Label>
                <Input
                  data-testid="edit-braces-input"
                  value={editing.braces_kit || ""}
                  onChange={(e) => setEditing({ ...editing, braces_kit: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Orthodontist</Label>
                <Select
                  value={editing.orthodontist || "none"}
                  onValueChange={(v) => setEditing({ ...editing, orthodontist: v === "none" ? "" : v })}
                >
                  <SelectTrigger data-testid="edit-ortho-select">
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="none">Unassigned</SelectItem>
                    {orthodontists.map((o) => (
                      <SelectItem key={o.id} value={o.name}>{o.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button data-testid="edit-save-button" onClick={saveEdit} className="bg-teal-600 hover:bg-teal-700 text-white rounded-lg">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!history} onOpenChange={(o) => !o && setHistory(null)}>
        <DialogContent className="bg-white sm:max-w-md" data-testid="history-modal">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <History className="h-5 w-5 text-accent" /> Visit History & Notes
            </DialogTitle>
            <DialogDescription>
              {history?.patient?.name} · {history?.patient?.register_number}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto -mx-1 px-1">
            {history?.loading ? (
              <p className="text-sm text-slate-400 py-6 text-center">Loading…</p>
            ) : history?.events?.length ? (
              <ol className="relative border-l border-slate-200 ml-3 space-y-4 py-2">
                {history.events.map((e, i) => {
                  const visited = e.type === "visited";
                  const date = new Date(e.created_at);
                  const when = isNaN(date) ? e.created_at : date.toLocaleString();
                  return (
                    <li key={i} className="ml-5" data-testid={`history-event-${i}`}>
                      <span
                        className={`absolute -left-[9px] flex h-4 w-4 items-center justify-center rounded-full ${
                          visited ? "bg-emerald-100" : "bg-red-100"
                        }`}
                      >
                        {visited ? (
                          <Check className="h-2.5 w-2.5 text-emerald-700" />
                        ) : (
                          <X className="h-2.5 w-2.5 text-red-700" />
                        )}
                      </span>
                      <div className="flex items-center gap-2">
                        <Badge
                          className={`rounded-full ${
                            visited
                              ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100"
                              : "bg-red-100 text-red-800 hover:bg-red-100"
                          }`}
                        >
                          {visited ? "Visited" : "No-Show"}
                        </Badge>
                        {e.batch && <span className="text-xs text-slate-500">Batch {e.batch}</span>}
                        {e.moved_to === "next_month" && (
                          <span className="text-xs text-violet-600">→ Next month</span>
                        )}
                        {e.moved_to && e.moved_to !== "next_month" && (
                          <span className="text-xs text-amber-600">→ Batch {e.moved_to}</span>
                        )}
                      </div>
                      <p className="text-[11px] text-slate-400 mt-1 flex items-center gap-1">
                        <Clock className="h-3 w-3" /> {when}
                      </p>
                      {e.id && (
                        <VisitNote
                          key={e.id}
                          patientId={history.patient.id}
                          event={e}
                          index={i}
                          onSaved={(note) =>
                            setHistory((h) => ({ ...h, events: h.events.map((x) => (x.id === e.id ? { ...x, note } : x)) }))
                          }
                        />
                      )}
                    </li>
                  );
                })}
              </ol>
            ) : (
              <p className="text-sm text-slate-400 py-8 text-center">
                No attendance history yet.
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!second} onOpenChange={(o) => !o && setSecond(null)}>
        <DialogContent className="bg-white sm:max-w-sm" data-testid="second-appt-modal">
          <DialogHeader>
            <DialogTitle className="font-heading">Second Appointment</DialogTitle>
            <DialogDescription>
              {second?.name} — schedule an extra visit in a different batch this month.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Batch for 2nd appointment</Label>
            <Select value={secondChoice || "none"} onValueChange={(v) => setSecondChoice(v === "none" ? "" : v)}>
              <SelectTrigger data-testid="second-appt-select">
                <SelectValue placeholder="Choose batch" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="none">No second appointment</SelectItem>
                {batches.filter((b) => b.label !== second?.batch).map((b) => (
                  <SelectItem key={b.label} value={b.label}>Batch {b.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button data-testid="second-appt-save" onClick={() => saveSecond(secondChoice || null)} className="bg-teal-600 hover:bg-teal-700 text-white rounded-lg">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
